/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import SecurityIcon from '@material-ui/icons/Security';
import WarningIcon from '@material-ui/icons/Warning';
import { HowItWorks, HowItWorksStep } from '../common/HowItWorks';
import { NetworkTopologyGraph } from './NetworkTopologyGraph';
import { ServiceDetailDrawer } from './ServiceDetailDrawer';
import { StagedChangesPanel, changeKey } from './StagedChangesPanel';
import { teamColor } from './ServiceFlowNode';
import { Change, NetworkPolicyGraph, hasTraefikLockdown } from './types';
import {
  useNetworkPolicyAccess,
  useNetworkPolicyApi,
} from './useNetworkPolicyApi';

const ADVANCED_KEY = 'm42.netpol.advanced';
const HOW_IT_WORKS_KEY = 'm42.netpol.howItWorks';

const READ_STEP: HowItWorksStep = {
  title: 'Read the graph',
  body: (
    <>
      Nodes are services, colored by team; an arrow runs from a caller to the
      service that admits it. The chips count services whose peer allow-list
      covers every environment, some of them, or none. Filter by team, then click
      a node for its callers, what it calls, and per-env detail.
    </>
  ),
};

// Edit steps describe surfaces only an editor sees (the drawer's actions and the
// staged panel), so read-only viewers get the source-of-truth step instead.
const EDIT_STEPS: HowItWorksStep[] = [
  READ_STEP,
  {
    title: 'Stage edits',
    body: (
      <>
        In the drawer: allow a new caller, remove one, restrict it to specific
        ports, or turn on default-deny for a service. Advanced also shows the
        per-env chart pin and offers promote-to-next-env. Every edit queues in
        the staged panel, nothing is applied yet.
      </>
    ),
  },
  {
    title: 'Review & open PRs',
    body: (
      <>
        The staged panel dry-runs each change against the real values files and
        shows the YAML diff, then opens one PR per affected repo. The policy
        only changes once those PRs merge and ArgoCD syncs.
      </>
    ),
  },
];

const READ_ONLY_STEPS: HowItWorksStep[] = [
  READ_STEP,
  {
    title: 'Change it in the repo',
    body: (
      <>
        You don't have network-policy edit access, so this view is read-only.
        The allow-list itself lives in the service repo, under{' '}
        <code>networkPolicy.dependencies.calledBy</code> in{' '}
        <code>gitops/argocd/values.yaml</code> and its per-env override, and can
        be PR'd there directly.
      </>
    ),
  },
];

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
  },
  canvas: {
    position: 'relative',
    height: 'calc(100vh - 280px)',
    minHeight: 480,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
  teamDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 4,
  },
  traefikDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.info.main,
    display: 'inline-block',
    marginRight: 4,
  },
}));

/**
 * The network-policy topology: what is allowed to call what (ingress edges
 * from `networkPolicy.dependencies.calledBy` across every service repo's
 * gitops values). Locked (default-deny everywhere) / partial / no-policy
 * nodes are visually distinct; edits are staged, dry-run previewed, and
 * submitted as one PR per affected repo.
 */
export const NetworkPolicyBody = () => {
  const classes = useStyles();
  const access = useNetworkPolicyAccess();
  const api = useNetworkPolicyApi();
  const [graph, setGraph] = useState<NetworkPolicyGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [staged, setStaged] = useState<Change[]>([]);
  const [advanced, setAdvanced] = useState(() => {
    try {
      return localStorage.getItem(ADVANCED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setAdvancedPersist = (v: boolean) => {
    setAdvanced(v);
    try {
      localStorage.setItem(ADVANCED_KEY, v ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const load = useCallback(
    async (refresh: boolean) => {
      setLoading(true);
      setError(null);
      try {
        setGraph(await api.getGraph(refresh));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    if (access.allowed) load(false);
  }, [access.allowed, load]);

  const teams = useMemo(
    () =>
      [...new Set((graph?.nodes ?? []).map(n => n.team).filter(Boolean))] as string[],
    [graph],
  );

  const counts = useMemo(() => {
    const nodes = graph?.nodes ?? [];
    const locked = nodes.filter(
      n => n.envs.length > 0 && n.envs.every(e => n.policyEnabled[e]),
    ).length;
    const open = nodes.filter(n => n.envs.every(e => !n.policyEnabled[e])).length;
    const traefik = nodes.filter(hasTraefikLockdown).length;
    return { locked, open, partial: nodes.length - locked - open, traefik };
  }, [graph]);

  if (access.allowed === false) {
    return <Alert severity="error">Sign-in required to view network policies.</Alert>;
  }

  return (
    <>
        {/* Deferred until the access probe lands, so a read-only viewer never
            sees the editor copy flash first. */}
        {access.allowed !== undefined && (
          <HowItWorks
            collapseKey={HOW_IT_WORKS_KEY}
            lead={
              <>
                Each service declares who may call it in its own repo, under{' '}
                <code>networkPolicy.dependencies.calledBy</code> in{' '}
                <code>gitops/argocd</code>. This page reads every service's
                values into one ingress graph.
                {access.canEdit &&
                  " Edits here never touch a cluster: they're staged, previewed as a diff, and shipped as PRs against the service repos."}
              </>
            }
            steps={access.canEdit ? EDIT_STEPS : READ_ONLY_STEPS}
          />
        )}
        <div className={classes.toolbar}>
          <Autocomplete
            options={teams}
            value={teamFilter}
            onChange={(_, v) => setTeamFilter(v)}
            style={{ width: 220 }}
            renderInput={params => (
              <TextField {...params} label="Team" size="small" variant="outlined" />
            )}
            renderOption={option => (
              <>
                <span
                  className={classes.teamDot}
                  style={{ background: teamColor(option) }}
                />
                {option}
              </>
            )}
          />
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => load(true)}
            disabled={loading}
          >
            Refresh
          </Button>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={advanced}
                onChange={e => setAdvancedPersist(e.target.checked)}
                color="primary"
              />
            }
            label="Advanced"
          />
          {!access.canEdit && access.allowed && (
            <Chip size="small" label="read-only" variant="outlined" />
          )}
          <div className={classes.legend}>
            <Tooltip title="Peer allow-list (calledBy) in every environment">
              <Chip
                size="small"
                icon={<SecurityIcon style={{ color: '#4caf50' }} />}
                label={`locked ${counts.locked}`}
                variant="outlined"
              />
            </Tooltip>
            <Tooltip title="Peer allow-list in some environments only">
              <Chip
                size="small"
                icon={<WarningIcon style={{ color: '#ff9800' }} />}
                label={`partial ${counts.partial}`}
                variant="outlined"
              />
            </Tooltip>
            <Tooltip title="No calledBy peer allow-list (may still have Traefik autoLockdown)">
              <Chip size="small" label={`no peers ${counts.open}`} variant="outlined" />
            </Tooltip>
            <Tooltip title="Chart autoLockdown: Traefik + baseline admitted; peer callers still need calledBy">
              <Chip
                size="small"
                icon={<span className={classes.traefikDot} />}
                label={`Traefik ${counts.traefik}`}
                variant="outlined"
              />
            </Tooltip>
            {graph && (
              <Typography variant="caption" color="textSecondary">
                as of {new Date(graph.generatedAt).toLocaleTimeString()}
              </Typography>
            )}
          </div>
        </div>

        {error && <Alert severity="error">{error}</Alert>}
        {!graph && !error && <Progress />}
        {graph && (
          <Box className={classes.canvas}>
            <NetworkTopologyGraph
              graph={graph}
              stagedChanges={staged}
              teamFilter={teamFilter}
              selected={selected}
              onSelect={setSelected}
            />
            <StagedChangesPanel
              changes={staged}
              onRemove={key => setStaged(s => s.filter(c => changeKey(c) !== key))}
              onClear={keepServices => {
                setStaged(s =>
                  keepServices && keepServices.size > 0
                    ? s.filter(c => keepServices.has(c.service))
                    : [],
                );
                load(true);
              }}
            />
          </Box>
        )}
        {graph && (
          <ServiceDetailDrawer
            service={selected}
            graph={graph}
            canEdit={access.canEdit}
            advanced={advanced}
            stagedChanges={staged}
            onStage={change => setStaged(s => [...s, change])}
            onClose={() => setSelected(null)}
          />
        )}
    </>
  );
};

/**
 * Standalone page kept for the legacy /network-policy route. The tabbed
 * Access shell renders NetworkPolicyBody directly, so the frame lives in
 * exactly one place per route rather than being nested twice.
 */
export const NetworkPolicyPage = () => (
  <Page themeId="tool">
    <Header
      title="Network Policies"
      subtitle="Ingress topology from gitops values (networkPolicy.dependencies.calledBy), edits open PRs"
    />
    <Content>
      <NetworkPolicyBody />
    </Content>
  </Page>
);
